<template>
  <div class="row d-flex align-items-center models-toolbar" ref="gridContainer">
    <div class="col-xl-auto col-12">
      <OrderTabs :active="orderBy" @select="changeOrder" />
    </div>
    <div class="col-xl col-12">
      <!-- no desktop a busca (.search-desktop, absoluta) divide esta linha com as abas:
           janela de 7 páginas = no máximo 13 botões, que cabem antes dela -->
      <vue-awesome-paginate
        class="justify-content-xl-start justify-content-sm-center"
        :modelValue="pageAtual"
        :total-items="paginations.total"
        :items-per-page="paginations.per_page"
        :max-pages-shown="isMobileBool ? 3 : 7"
        @click="onClickHandler"
        paginate-buttons-class="btn"
      />
    </div>
  </div>
  <CategoryChips v-if="categories.length" :categories="categories" :activeSlug="activeCategory" />
  <div class="row align-items-center mt-4">
    <div v-for="model in arrayModels" class="col-lg-2" :key="model.id">
      <div class="card-pagination pr-3 pb-2">
        <!-- Capa: o link cobre só imagem+vídeo. Desktop (≥970) clica → navega.
             Mobile/tablet (<970): 1º toque revela+play (não navega), 2º toque no vídeo navega. -->
        <NuxtLink
          :to="{name: 'language-modelos-name-photo', params: {name: model.slug}}"
          custom
          v-slot="{ href, navigate }"
        >
          <a
            :href="href"
            class="cover-link"
            :aria-label="model.title"
            @click="onCoverClick($event, navigate, model.id, !!model.preview)"
          >
            <div
              class="img-box"
              :class="{ 'is-playing': revealed.has(model.id) }"
              @mouseenter="playVideo"
              @mouseleave="pauseVideo"
            >
              <img
                :src="`${model.cover}`"
                :class="{ 'imagem-estatica': model.preview }"
                :alt="model.imageAltText ? model.imageAltText : model.title"
                width="227"
              />
              <video v-if="model.preview"
                class="gif"
                playsInline
                muted
                loop
                >
                <source :src="model.preview" type="video/mp4">
              </video>
            </div>
          </a>
        </NuxtLink>
        <!-- Nome: link separado, sempre navega -->
        <div class="card-body d-flex flex-column align-items-center">
          <NuxtLink
            :to="{name: 'language-modelos-name-photo', params: {name: model.slug}}"
            class="name-link"
          >
            <h5 class="card-title mb-3 mt-2">{{ model.title }}</h5>
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
  <PaginationControls
    :current-page="pageAtual"
    :last-page="lastPage"
    :label="translate.nextPage"
    :prev-aria-label="translate.prevPage"
    :next-aria-label="translate.nextPage"
    :go-to-page="onClickHandler"
    :scroll-target="gridContainer"
  />
</template>

<script setup lang="ts">
import modelsTradutor from "@/tradutors/models";
import { isMobile } from "@/shared/utils/mobile";
import api from "@/api";
import CategoryChips from "@/components/Modelos/CategoryChips.vue";
import PaginationControls from "@/components/Modelos/PaginationControls.vue";
import OrderTabs from "@/components/Modelos/OrderTabs.vue";

interface Model {
  id: number;
  title: string;
  slug: string;
  cover: string;
  gif?: string;
  preview?: string;
  imageAltText?: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  modelCount: number;
}

interface Pagination {
  total: number;
  per_page: number;
  data: Model[];
  current_page: number;
}
const props = defineProps({
  models: {
    type: Object as () => Pagination,
    required: true,
  },
  apiEndpoint: {
    type: String,
    default: "v1/modelos/getAllModels",
  },
  categories: {
    type: Array as () => Category[],
    default: () => [],
  },
  activeCategory: {
    type: String as () => string | null,
    default: null,
  },
});

const storeUser = userStore();
const route = useRoute();
const translate = computed(() =>
  (route.params.language) === "pt-br"
    ? modelsTradutor.portugues
    : modelsTradutor.ingles
);

const isMobileBool = computed(() => isMobile());
const pageAtual = ref(props.models?.current_page ?? 1);
const arrayModels = ref<any>(props.models?.data ?? []);
const paginations = ref<any>(props.models ?? {
  total: 1,
  per_page: 1,
});
const orderBy = ref("order");
const gridContainer = ref<HTMLElement | null>(null);

const DESKTOP_MIN = 970;
const revealed = reactive(new Set<number>());

const lastPage = computed(() =>
  Math.max(
    1,
    Math.ceil((paginations.value.total || 0) / (paginations.value.per_page || 1))
  )
);

function coverVideo(e: Event): HTMLVideoElement | null {
  return (e.currentTarget as HTMLElement).querySelector(".gif") as HTMLVideoElement | null;
}

// hover só existe no desktop (≥970); o guard neutraliza mouseenter sintético no touch
function playVideo(e: MouseEvent) {
  if (window.innerWidth >= DESKTOP_MIN) coverVideo(e)?.play().catch(() => {});
}
function pauseVideo(e: MouseEvent) {
  if (window.innerWidth >= DESKTOP_MIN) coverVideo(e)?.pause();
}

function onCoverClick(
  event: MouseEvent,
  navigate: (e?: MouseEvent) => void,
  id: number,
  hasPreview: boolean
) {
  // desktop: clicar em qualquer parte navega (SPA). navigate() faz preventDefault
  // no clique normal e respeita ctrl/cmd/clique-do-meio (abre a URL real via href).
  if (window.innerWidth >= DESKTOP_MIN) return navigate(event);
  // sem vídeo, ou vídeo já revelado → navega
  if (!hasPreview || revealed.has(id)) return navigate(event);
  // 1º toque: pausa/reseta os outros vídeos do grid e revela só este (1 por vez)
  event.preventDefault();
  const target = event.currentTarget as HTMLElement;
  pauseSiblingVideos(target);
  revealed.clear();
  revealed.add(id);
  target.querySelector<HTMLVideoElement>(".gif")?.play().catch(() => {});
}

// pausa e reseta todos os vídeos do mesmo grid/carrossel (garante 1 vídeo por vez)
function pauseSiblingVideos(clicked: HTMLElement) {
  const container = clicked.closest(".row, .carousel");
  container?.querySelectorAll<HTMLVideoElement>(".gif").forEach((v) => {
    v.pause();
    if (v.currentTime) v.currentTime = 0;
  });
}

async function onClickHandler(page: number) {
  pageAtual.value = page;
  await getNewPage(page);
}
async function getNewPage(page: number) {
  try {
    const { data } = await api.get(
      props.apiEndpoint + "?order=" + orderBy.value + "&page=" + page + "&language=" + route.params.language
    );
    const { modelsArray } = data;
    paginations.value = modelsArray;
    arrayModels.value = modelsArray.data;
    revealed.clear();
  } catch (error) {
    console.error('Failed to fetch paginated models', error);
  }
}
function changeOrder(order: string) {
  orderBy.value = order;
  onClickHandler(1);
}
</script>

<style scoped lang="css">
.pagination-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 10px;
}

.paginate-buttons {
  height: 40px;
  width: 40px;
  border-radius: 20px;
  cursor: pointer;
  background-color: rgb(242, 242, 242);
  border: 1px solid rgb(217, 217, 217);
  color: black;
}

.paginate-buttons:hover {
  background-color: #d8d8d8;
}

.active-page {
  background-color: #b20000;
  border: 1px solid #b20000;
  color: white;
}

.active-page:hover {
  background-color: #b20000;
}

ul#componentContainer {
  display: flex !important;
  justify-content: center;
}

.card-title {
  height: 19px;
  color: #666666;
  font-family: "Exo-2";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
  text-align: center;
}

.img-box {
  position: relative;
  width: 100%;
}

.img-box img {
  display: block;
  width: 100%;
  height: auto;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Tamanho do ícone de reprodução */
  color: white;
  /* Cor do ícone de reprodução */
  cursor: pointer;
}

.card-pagination {
  background-color: transparent;
}

.col-lg-2 {
  width: 20%;
}

/* imagem estática em fluxo: define o retângulo (largura/altura) do card */
.imagem-estatica {
  position: relative;
  transition: opacity 0.3s;
}
/* vídeo sobreposto: cobre exatamente a imagem em qualquer breakpoint */
.gif {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0; /* Inicialmente invisível */
  transition: opacity 0.3s; /* Transição suave */
  z-index: 2;
}

/* reveal por toque (<970): a classe só é adicionada quando innerWidth < 970,
   por isso a regra pode ficar no base (cobre também 770–969) */
.is-playing .gif {
  opacity: 1;
}
.is-playing .imagem-estatica {
  opacity: 0;
}

.cover-link {
  display: block;
  text-decoration: none;
}
.name-link {
  text-decoration: none;
  color: inherit;
}

:deep(.active-page:hover) {
  background-color: #b20000 !important;
  color: white !important;
}

:deep(.number-buttons) {
  font-size: 15px;
}
:deep(.pagination-container li) {
  width: 25px;
}
/* reveal por hover só no desktop (≥970), SEM zoom (igual ao CarouselModels) */
@media screen and (min-width: 970px) {
  .img-box:hover .gif {
    opacity: 1;
  }
  .img-box:hover .imagem-estatica {
    opacity: 0;
  }
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
  .col-lg-2 {
    width: 50%;
  }

  :deep(.pagination-container .btn) {
    font-size: 13px !important;
    padding: 5px 8px;
  }
}

@media screen and (min-width: 770px) and (max-width: 900px) {
  .col-lg-2 {
    width: 33.33%;
  }
}

@media screen and (min-width: 901px) and (max-width: 1199px) {
  .col-lg-2 {
    width: 25%;
  }
}

/* tablets e iPads: empilha as abas de ordem sobre a paginação */
@media screen and (min-width: 770px) and (max-width: 1300px) {
  .models-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .models-toolbar > [class*="col-"] {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
  }

  /* centraliza rótulos e paginação e mantém o espaçamento entre as linhas */
  .models-toolbar .justify-content-xl-start {
    justify-content: center !important;
  }

  .models-toolbar > [class*="col-"]:first-child .d-flex {
    margin-bottom: 1rem !important;
  }
}

</style>
